import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Dialog, DialogModule } from 'primeng/dialog';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { EditorModule } from 'primeng/editor';
import { of, forkJoin, take, lastValueFrom, firstValueFrom } from 'rxjs';
import { catchError, finalize, timeout } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { Vehicle } from 'src/app/models/vehicle';
import { FileDto, Restriction } from 'src/app/models/restriction';
import { Fpc } from 'src/app/models/fpc';

import { VehicleSelectorComponent } from '../vehicle-selector/vehicle-selector.component';
import { CategorySelectorComponent } from '../category-selector/category-selector.component';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { LinkListComponent } from '../link-list/link-list.component';

import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { FileService } from 'src/app/services/file.service';
import { RestrictionsService } from 'src/app/services/restrictions.service';
import { AuthService } from 'src/app/services/auth.service';
import { VehiclesService } from 'src/app/services/vehicles.service';
import { CategoriesService } from 'src/app/services/categories.service';

import { DrivingRestrictionMutations } from 'src/app/graphql/mutations/restrictions.mutations';
import { GraphqlRestrictionInputDto } from 'src/app/graphql/models/input/restriction-input.model';
import { UploadedFile } from 'src/app/models/fileUpload';
import { dateRangeCompleteValidator } from 'src/app/shared/range-complete.validator';

@Component({
  selector: 'app-restriction-dialog',
  standalone: true,
  imports: [
    Dialog,
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    FileUploadModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    DatePickerModule,
    VehicleSelectorComponent,
    CategorySelectorComponent,
    FileUploadComponent,
    LinkListComponent,
    EditorModule
  ],
  templateUrl: './restriction-dialog.component.html',
  styleUrl: './restriction-dialog.component.scss'
})
export class RestrictionDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() editRestriction: Restriction | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSave = new EventEmitter<any>();

  restrictionForm: FormGroup;
  vehicleDialogVisible = false;
  loadingFiles = false;
  saving = false;
  showEndRestrictionConfirmDialog = false;

  // initial files for each uploader
  initialPictureFiles: UploadedFile[] = [];
  initialDocumentFiles: UploadedFile[] = [];

  selectedVehicles: Vehicle[] = [];
  randomNumber: number | null = null;

  selectedFpcs: Fpc[] = [];
  categoryDialogVisible = false;

  handleCategoryConfirm(fpcs: Fpc[]): void {
    this.selectedFpcs = fpcs;
  }

  removeFpc(fpc: Fpc): void {
    this.selectedFpcs = this.selectedFpcs.filter(f => f.fpcCode !== fpc.fpcCode);
  }

  fpcCodeDisplay(fpcCode: string): string {
    return fpcCode.replace(/^FPC/i, '');
  }

  // Monday of the first week clicked in the week-number column; null when no week-range is in progress
  private weekRangeStart: Date | null = null;
  // True only while onCalendarClick is writing the range, so its own valueChanges emission doesn't clear weekRangeStart
  private settingWeekRange = false;

  @ViewChild('linkListRef') linkListRef!: LinkListComponent;
  @ViewChild('pictureUploader') pictureUploader!: FileUploadComponent;
  @ViewChild('documentUploader') documentUploader!: FileUploadComponent;

  // image extension helper
  private readonly imageExt = new Set(['png','jpg','jpeg','gif','webp','bmp','svg','heic','heif','tif','tiff']);

  // Default HTML template texts for form fields (used in p-editor / Quill)
  // Each bold label is in its own <p>, followed by <p>&#8203;</p> (zero-width space) to reset bold context
  static readonly DEFAULTS = {
    approvedBy: [
      `<p><strong>Responsible for restriction document:</strong></p><p>&#8203;</p>`,
      `<p><strong>Responsible for causing part:</strong></p><p>&#8203;</p>`,
      `<p><strong>Driving restriction applies at startup:</strong> Y/N</p><p>&#8203;</p>`,
      `<p><strong>Responsible vehicle coordinator:</strong></p><p>&#8203;</p>`
    ].join(''),
    causing: [
      `<p><strong>Affected Vehicles/Systems/Component/SW:</strong></p><p>&#8203;</p>`,
      `<p><strong>Vehicle name:</strong></p><p>&#8203;</p>`,
      `<p><strong>Software version:</strong></p><p>&#8203;</p>`,
      `<p><strong>Component:</strong></p><p>&#8203;</p>`,
      `<p><strong>System:</strong></p><p>&#8203;</p>`
    ].join(''),
    risk: [
      `<p><strong>Background information:</strong></p><p>&#8203;</p>`,
      `<p><strong>Is there a combination or dependencies of system/components that makes the risk:</strong></p><p>&#8203;</p>`
    ].join(''),
    consequence: [
      `<p><strong>Risk descriptions general:</strong></p><p>&#8203;</p>`,
      `<p><strong>Functions/systems that will be affected:</strong></p><p>&#8203;</p>`,
      `<p><strong>When/during what circumstances do the risk occur:</strong></p><p>&#8203;</p>`,
      `<p><strong>Vehicle response/behaviour:</strong></p><p>&#8203;</p>`,
      `<p><strong>What to pay attention to:</strong></p><p>&#8203;</p>`,
      `<p><strong>Risk category:</strong> Fire hazard, human hazard, Vehicle Off Road: Loss of propulsion/Auxiliary brake.</p>`
    ].join(''),
    precondition: [
      `<p>Skilled driver who has gone through course(link) and have good knowledge about handling logging computer and error fault handling.</p><p>&#8203;</p>`,
      `<p><strong>Need of approved drivers list:</strong> Yes/No</p><p>&#8203;</p>`,
      `<p><strong>Knowledge about affected systems:</strong> Instruction/description/checklist needed? Contact person.</p><p>&#8203;</p>`,
      `<p><strong>Logging systems:</strong></p><p>&#8203;</p>`
    ].join(''),
    operatedRestriction: [
      `<p><strong>Description of the correct behaviour by driver/operator</strong></p><p>&#8203;</p>`,
      `<p><strong>Max load:</strong> &nbsp; <strong>Max speed:</strong> &nbsp; <strong>Max/min temperature:</strong></p><p>&#8203;</p>`,
      `<p><strong>SW/System version:</strong> Other SW/systems that minimize the risk.</p><p>&#8203;</p>`,
      `<p><strong>Max charging current:</strong></p><p>&#8203;</p>`,
      `<p><strong>Road Surface:</strong> (No shaker ladders)</p><p>&#8203;</p>`,
      `<p><strong>Other:</strong></p><p>&#8203;</p>`
    ].join(''),
    removingCondition: [
      `<p><strong>Date or Complete Vehicle Integration Test CVIT week</strong></p><p>&#8203;</p>`,
      `<p><strong>Part number and/or version</strong></p><p>&#8203;</p>`,
      `<p><strong>Description of the required update of hardware, software or system</strong></p><p>&#8203;</p>`
    ].join(''),
    additionalInformation: [
      `<p><strong>Link to presentations or documents:</strong> FRAS, Drawing, PD, Jira, other</p><p>&#8203;</p>`,
      `<p><strong>Link to logging instructions:</strong></p><p>&#8203;</p>`,
      `<p><strong>Risk assessment document:</strong></p><p>&#8203;</p>`,
      `<p><strong>Early signals / indications of the deviation:</strong> Fault codes, changes in performance, warnings</p><p>&#8203;</p>`,
      `<p><strong>Additional pictures</strong></p><p>&#8203;</p>`,
      `<p><strong>Troubleshooting guide / how to contact:</strong> Support if deviation occurs.</p><p>&#8203;</p>`,
      `<p><strong>Overview picture:</strong></p><p>&#8203;</p>`,
      `<p><strong>Known deviations and risks:</strong></p><p>&#8203;</p>`
    ].join('')
  };

  /**
   * Strips HTML tags and whitespace to get plain text content for comparison.
   */
  private static extractText(html: string): string {
    return (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&#8203;/g, '').replace(/\u200B/g, '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Validator that checks if the user has added text beyond the pre-filled default template.
   */
  private static contentBeyondDefault(defaultHtml: string) {
    const defaultText = RestrictionDialogComponent.extractText(defaultHtml);
    return (control: { value: any }) => {
      const currentText = RestrictionDialogComponent.extractText(control.value || '');
      return currentText !== defaultText ? null : { contentRequired: true };
    };
  }

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private restrictionsService: RestrictionsService,
    private apolloClientService: ApolloClientService,
    private fileService: FileService,
    private vehiclesService: VehiclesService,
    private categoriesService: CategoriesService
  ) {
    this.restrictionForm = this.fb.group({
      id: [null],
      name: ['', Validators.required],
      banTestTrack: [false],
      banOpenRoad: [false],
      validRange: this.fb.control<Date[] | null>(null, [dateRangeCompleteValidator]),
      causing: [RestrictionDialogComponent.DEFAULTS.causing, RestrictionDialogComponent.contentBeyondDefault(RestrictionDialogComponent.DEFAULTS.causing)],
      risk: [RestrictionDialogComponent.DEFAULTS.risk, RestrictionDialogComponent.contentBeyondDefault(RestrictionDialogComponent.DEFAULTS.risk)],
      consequence: [RestrictionDialogComponent.DEFAULTS.consequence, RestrictionDialogComponent.contentBeyondDefault(RestrictionDialogComponent.DEFAULTS.consequence)],
      precondition: [RestrictionDialogComponent.DEFAULTS.precondition, RestrictionDialogComponent.contentBeyondDefault(RestrictionDialogComponent.DEFAULTS.precondition)],
      operatedRestriction: [RestrictionDialogComponent.DEFAULTS.operatedRestriction, RestrictionDialogComponent.contentBeyondDefault(RestrictionDialogComponent.DEFAULTS.operatedRestriction)],
      removingCondition: [RestrictionDialogComponent.DEFAULTS.removingCondition, RestrictionDialogComponent.contentBeyondDefault(RestrictionDialogComponent.DEFAULTS.removingCondition)],
      additionalInformation: [RestrictionDialogComponent.DEFAULTS.additionalInformation],
      active: [false],
      documents: [[]],
      pictures: [[]],
      links: [[]],
      vehicles: [[], [Validators.required, this.atLeastOneVehicleValidator]],
      createdBy: [''],
      modifiedBy: [''],
      approvedBy: [RestrictionDialogComponent.DEFAULTS.approvedBy, RestrictionDialogComponent.contentBeyondDefault(RestrictionDialogComponent.DEFAULTS.approvedBy)],
      removedVehicleName: [[]],
      fileUidsForRemove: [[]]
    });

    // Any change to the range that doesn't come from a week-number click (manual day picking,
    // patching an edit, clearing) abandons an in-progress week selection, so the next week click
    // starts fresh instead of extending from a stale Monday.
    this.restrictionForm.get('validRange')?.valueChanges.subscribe(() => {
      if (!this.settingWeekRange) this.weekRangeStart = null;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      // Warm the vehicles/FPCs caches as soon as the dialog opens, so Add Vehicles/Add FPCs
      // don't block on the fetch when the user clicks them.
      this.vehiclesService.getAllVehicles$().pipe(take(1)).subscribe();
      this.categoriesService.getFpcFamilies$().pipe(take(1)).subscribe();
    }
    if (changes['editRestriction']?.currentValue) {
      // Delay patching to allow p-editor (Quill) instances to initialize after dialog opens
      setTimeout(() => {
        this.patchFormWithData(changes['editRestriction'].currentValue);
      });
      const uids = this.editRestriction?.fileUids ?? [];
      if (uids.length) {
        this.loadFileNamesIntoUploaders(uids);
      } else {
        this.initialPictureFiles = [];
        this.initialDocumentFiles = [];
        this.restrictionForm.patchValue({ pictures: [], documents: [] });
      }
    }
  }

  private async getCurrentUserName(): Promise<string> {
    const ud = await firstValueFrom(this.authService.userData$.pipe(take(1)));
    return ud?.userName ?? localStorage.getItem('userName') ?? '';
  }

  private isImageName(name: string): boolean {
    const ext = (name?.split('.').pop() || '').toLowerCase();
    return this.imageExt.has(ext);
  }

  private loadFileNamesIntoUploaders(fileDtos: FileDto[]) {
    this.loadingFiles = true;

    forkJoin(
      fileDtos.map(uid =>
        this.fileService.retrieveFileInfo(uid.fileUid).pipe(
          catchError(() => of({ FileUid: uid.fileUid, FileName: '(unknown)' }))

        )
      )
    )
    .pipe(finalize(() => (this.loadingFiles = false)))
    .subscribe(infos => {
      const isImg = (n: string) => this.isImageName(n);
      const images = infos.filter(i => isImg(i.FileName));
      const docs   = infos.filter(i => !isImg(i.FileName));

      this.initialPictureFiles  = images.map(i => ({ file: null, name: i.FileName, isSaved: true, uid: i.FileUid }));
      this.initialDocumentFiles = docs.map(i => ({ file: null, name: i.FileName, isSaved: true, uid: i.FileUid }));

      // make form reflect what uploaders show
      this.restrictionForm.patchValue({
        pictures: this.initialPictureFiles,
        documents: this.initialDocumentFiles
      });
    });
  }

  patchFormWithData(data: Restriction) {
    const linkArray = data.links ? data.links.split(',') : [];

    this.restrictionForm.patchValue({
      id: data.restrictionId,
      name: data.restrictionName,
      banTestTrack: data.banTestTrack,
      banOpenRoad: data.banPublicRoad,
      validRange: data.validFrom && data.validTo ? [new Date(data.validFrom), new Date(data.validTo)] : null,
      causing: data.cause,
      risk: data.risk,
      consequence: data.consequence,
      precondition: data.precondition,
      operatedRestriction: data.operatedRestriction,
      removingCondition: data.removingCondition,
      additionalInformation: data.additionalInformation,
      createdBy: data.createdBy,
      modifiedBy: data.modifiedBy,
      links: linkArray,
      active: !data.active,
      approvedBy: data.approvedBy,
      removedVehicleName: data.removedVehicleName,
      fileUidsForRemove: data.fileUidsForRemove
    });

    this.selectedVehicles = (data.vehicles ?? []).map(v => ({ vinNo: v.vinNo, name: v.name })) as Vehicle[];
    this.restrictionForm.patchValue({ vehicles: this.selectedVehicles });

    this.selectedFpcs = (data.fpcs ?? []).map(f => {
      const codeDisplay = f.fpcId.replace(/^FPC/i, '');
      const cleanName = f.name.replace(new RegExp(`^${codeDisplay}\\s*-\\s*`, 'i'), '');
      return { fpcCode: f.fpcId, name: cleanName };
    });

    this.linkListRef?.ngOnChanges({
      initialLinks: {
        currentValue: linkArray,
        previousValue: [],
        firstChange: true,
        isFirstChange: () => true
      }
    });
  }

  /**
   * Makes the calendar's week-number column selectable. PrimeNG only displays week
   * numbers, so we intercept clicks on a week-number cell and derive the week's
   * Monday→Sunday from the day cells rendered in the same row.
   *
   * First week click selects that whole week. A second week click extends the
   * selection to the end of that week (e.g. week 12 then week 17 → Mon wk12 → Sun wk17).
   */
  onCalendarClick(event: Event) {
    const target = event.target as HTMLElement;
    const weekCell = target.closest('.p-datepicker-weeknumber');
    if (!weekCell) return;

    const row = weekCell.closest('tr');
    if (!row) return;

    const dayKeys = Array.from(row.querySelectorAll('span[data-date]'))
      .map(el => el.getAttribute('data-date'))
      .filter((k): k is string => !!k);
    if (!dayKeys.length) return;

    const monday = this.parseDateKey(dayKeys[0]);
    const sunday = this.parseDateKey(dayKeys[dayKeys.length - 1]);

    const ctrl = this.restrictionForm.get('validRange');

    this.settingWeekRange = true;
    if (!this.weekRangeStart) {
      // First week click: select the full week and remember its start for a possible extension.
      this.weekRangeStart = monday;
      ctrl?.setValue([monday, sunday]);
    } else {
      // Second week click: span from the earlier week's Monday to the later week's Sunday.
      const start = monday < this.weekRangeStart ? monday : this.weekRangeStart;
      const end = monday < this.weekRangeStart ? this.weekEndFor(this.weekRangeStart) : sunday;
      ctrl?.setValue([start, end]);
      this.weekRangeStart = null;
    }
    this.settingWeekRange = false;
    ctrl?.markAsTouched();
  }

  /** Parses PrimeNG's `year-month-day` key (month is 0-indexed) into a Date. */
  private parseDateKey(key: string): Date {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m, d);
  }

  /** Sunday (end) of the ISO week containing the given Monday. */
  private weekEndFor(monday: Date): Date {
    const end = new Date(monday);
    end.setDate(end.getDate() + 6);
    return end;
  }

  atLeastOneVehicleValidator(control: any) {
    return control.value && control.value.length > 0 ? null : { required: true };
  }

  async save(event?: Event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (this.saving) return;
    if (this.restrictionForm.invalid) { this.restrictionForm.markAllAsTouched(); return; }

    const isEdit = !!this.editRestriction;
    if (!isEdit) {
      if (!this.selectedVehicles.length || this.selectedVehicles.some(v => !v?.name)) {
        this.restrictionForm.get('vehicles')?.setErrors({ required: true });
        return;
      }
    }

    this.saving = true;
    try {
      // Files still present in UI and originally saved (we keep their UIDs)
      const keptSavedUids: string[] = [
        ...this.getFormFiles('documents'),
        ...this.getFormFiles('pictures'),
      ]
        .filter(f => f.isSaved && !!f.uid)
        .map(f => f.uid!);

      // Files newly uploaded this session (we’ll convert to FileDto[])
      const newFileDtos: FileDto[] = await this.uploadAllNewFilesAsync();

      const userName = await this.getCurrentUserName();
      const restrictionDto = this.generateRestrictionDto({
        keptSavedUids,
        newUids: newFileDtos,
        userName
      });

      const mutation = isEdit
        ? DrivingRestrictionMutations.editRestriction
        : DrivingRestrictionMutations.createRestriction;

      const client = await lastValueFrom(this.apolloClientService.abstractionLayerClient$.pipe(take(1)));
      await client.mutate<
        { drivingRestrictionCreateRestriction?: string; drivingRestrictionEditDrivingRestriction?: string },
        { restrictionDto: GraphqlRestrictionInputDto }
      >({
        mutation,
        variables: { restrictionDto },
        context: { headers: { 'ShowLoading': 'true' } },
        errorPolicy: 'all'
      }).then(res => {
        if (res.errors?.length) throw new Error(res.errors.map(e => e.message).join(' | '));
      });

      this.restrictionsService.refreshRestrictions();
      this.onSave.emit(this.restrictionForm.value);
      this.close();
    } catch (err) {
      console.error('Failed to save restriction:', err);
    } finally {
      this.saving = false;
    }
  }

  clear() {
    this.restrictionForm.reset({
      name: '',
      banTestTrack: false,
      banOpenRoad: false,
      validRange: null,
      active: false,
      causing: RestrictionDialogComponent.DEFAULTS.causing,
      risk: RestrictionDialogComponent.DEFAULTS.risk,
      consequence: RestrictionDialogComponent.DEFAULTS.consequence,
      precondition: RestrictionDialogComponent.DEFAULTS.precondition,
      operatedRestriction: RestrictionDialogComponent.DEFAULTS.operatedRestriction,
      removingCondition: RestrictionDialogComponent.DEFAULTS.removingCondition,
      additionalInformation: RestrictionDialogComponent.DEFAULTS.additionalInformation,
      createdBy: '',
      modifiedBy: '',
      approvedBy: RestrictionDialogComponent.DEFAULTS.approvedBy,
      links: [],
      vehicles: [],
      removedVehicleName: [],
      fileUidsForRemove: [],
    });

    this.selectedVehicles = [];
    this.selectedFpcs = [];
    this.weekRangeStart = null;
    this.initialPictureFiles = [];
    this.initialDocumentFiles = [];
    this.linkListRef?.reset();
    this.pictureUploader?.reset();
    this.documentUploader?.reset();
  }

  close() {
    this.clear();
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  // p-dialog's onHide fires only after the leave animation ends, well after `visible`
  // flips to false. If the dialog is reopened during that window, this stale callback
  // must not clear the freshly patched form - only finalize the close if nothing reopened it.
  onDialogHide() {
    if (this.visible) return;
    this.close();
  }

  endRestriction() {
    this.showEndRestrictionConfirmDialog = true;
  }

  cancelEndRestriction() {
    this.showEndRestrictionConfirmDialog = false;
  }

  confirmEndRestriction() {
    const restrictionId = this.editRestriction?.restrictionId;
    if (!restrictionId) return;

    this.restrictionsService.endRestriction$(restrictionId).subscribe({
      next: () => {
        this.showEndRestrictionConfirmDialog = false;
        this.restrictionsService.refreshRestrictions();
        this.close();
      },
      error: () => {
        this.showEndRestrictionConfirmDialog = false;
      }
    });
  }

  onUpload(files: UploadedFile[], field: 'documents'|'pictures') {
    this.restrictionForm.patchValue({ [field]: files });
  }

  private toFilesArray(val: any): UploadedFile[] {
    if (Array.isArray(val)) return val as UploadedFile[];
    if (val && Array.isArray(val.files)) return val.files as UploadedFile[];
    return [];
  }

  private getFormFiles(field: 'documents' | 'pictures'): UploadedFile[] {
    const raw = this.restrictionForm.get(field)?.value;
    return this.toFilesArray(raw);
  }

  private sanitizeFileName(name: string): string {
    const trimmed = (name ?? '').trim().slice(-120);
    const asciiSafe = trimmed.normalize('NFKD').replace(/[^\w.\-]/g, '');
    return asciiSafe || `file_${Date.now()}`;
  }

  private async uploadOneAsync(f: UploadedFile): Promise<string> {
    try {
      if (!f?.file) return '';
      const rawName = f.file.name || f.name || `upload-${Date.now()}`;
      const fileName = this.sanitizeFileName(rawName);

      // ask backend for (FileUid, PreSignUrl)
      const handshake = await firstValueFrom(
        this.fileService.requestUploadFile(fileName, 'DrivingRestriction')
      );
      if (!handshake?.FileUid || !handshake?.PreSignUrl) return '';

      // PUT to S3
      await firstValueFrom(
        this.fileService.uploadFile(f, handshake.PreSignUrl).pipe(timeout(120000))
      );

      f.isSaved = true;
      f.uid = handshake.FileUid;
      return handshake.FileUid;
    } catch {
      return '';
    }
  }

  /** Upload new files and return them as FileDto[] */
  private async uploadAllNewFilesAsync(): Promise<FileDto[]> {
    const docs = this.getFormFiles('documents');
    const pics = this.getFormFiles('pictures');
    const candidates = [...docs, ...pics].filter(x => !!x?.file);
    if (!candidates.length) return [];

    const results = await Promise.all(candidates.map(f => this.uploadOneAsync(f).catch(() => '')));
    const uniqueUids = Array.from(new Set(results.filter(Boolean)));
    return uniqueUids.map(uid => ({ fileUid: uid as string }));
  }

  onLinksChanged(links: string[]) {
    this.restrictionForm.patchValue({ links });
  }

  get selectedVehicleNames(): string {
    return this.selectedVehicles.map(v => v.name).join(', ');
  }

  handleVehicleConfirm(selected: Vehicle[]) {
    this.selectedVehicles = selected;
    this.restrictionForm.patchValue({ vehicles: selected });
  }

  onAddVehiclesClick(ev: Event) {
    ev.preventDefault();
    ev.stopPropagation();
    this.vehicleDialogVisible = true;
  }

  private diffVehiclesForEdit(): {
    additions: Array<{ vinNo: string; name: string }>;
    removals: string[];
  } {
    const originalNames = new Set((this.editRestriction?.vehicles ?? []).map(v => v.name));
    const currentByName = new Map((this.selectedVehicles ?? []).map(v => [v.name, v.vinNo]));

    const additions: Array<{ vinNo: string; name: string }> = [];
    const removals: string[] = [];

    for (const [name, vinNo] of currentByName) {
      if (!originalNames.has(name)) additions.push({ vinNo, name });
    }
    for (const name of originalNames) {
      if (!currentByName.has(name)) removals.push(name);
    }
    return { additions, removals };
  }

  private diffFpcsForEdit(): {
    additions: Array<{ fpcId: string; name: string; category: string }>;
    removals: string[];
  } {
    const originalIds = new Set((this.editRestriction?.fpcs ?? []).map(f => f.fpcId));
    const currentIds = new Set(this.selectedFpcs.map(f => f.fpcCode));

    const additions = this.selectedFpcs
      .filter(f => !originalIds.has(f.fpcCode))
      .map(f => ({ fpcId: f.fpcCode, name: f.name, category: '' }));

    const removals = [...originalIds].filter(id => !currentIds.has(id));

    return { additions, removals };
  }

  private buildFileDiffForEdit(keptSavedUids: string[], newUids: FileDto[]) {
    const originalFds: FileDto[] = this.editRestriction?.fileUids ?? [];
    const keptSet = new Set(keptSavedUids);

    // Removals: original file UIDs that are no longer kept
    const fileUidsForRemove = originalFds
      .map(fd => fd.fileUid)
      .filter(uid => !keptSet.has(uid));

    // Additions: unique-by fileUid from newUids
    const additionsMap = new Map<string, FileDto>();
    for (const fd of newUids) additionsMap.set(fd.fileUid, fd);
    const additionsUnique: FileDto[] = Array.from(additionsMap.values());

    // If no new files added in edit, send fileUids: null
    const fileUidsForDto: FileDto[] | null = additionsUnique.length ? additionsUnique : null;

    return { fileUidsForDto, fileUidsForRemove };
  }

  private generateRestrictionDto(
    args: { keptSavedUids?: string[]; newUids?: FileDto[]; userName?: string } = {}
  ): GraphqlRestrictionInputDto {
    const { keptSavedUids = [], newUids = [], userName = '' } = args;

    const [validFrom, validTo] = this.restrictionForm.value.validRange || [];
    const formValue = this.restrictionForm.value;

    const toIso = (d: any): string | undefined => {
      if (!d) return undefined;
      if (d instanceof Date) return d.toISOString();
      const t = new Date(d);
      return isNaN(t.getTime()) ? undefined : t.toISOString();
    };

    const isEdit = !!this.editRestriction;

    // ---------- VEHICLE DIFF ----------
    let vehiclesForDto: Array<{ vinNo: string; name: string }> | null;
    let removedVehicleNameForDto: string[];
    if (isEdit) {
      const { additions, removals } = this.diffVehiclesForEdit();
      vehiclesForDto = additions.length ? additions : null;
      removedVehicleNameForDto = removals;
    } else {
      vehiclesForDto = this.selectedVehicles.map(v => ({ vinNo: v.vinNo, name: v.name }));
      removedVehicleNameForDto = [];
    }

    // ---------- FILE DIFF ----------
    let fileUidsForDto: FileDto[] | null;
    let fileUidsForRemove: string[];
    if (isEdit) {
      const fileDiff = this.buildFileDiffForEdit(keptSavedUids, newUids);
      fileUidsForDto = fileDiff.fileUidsForDto;          // null if no additions
      fileUidsForRemove = fileDiff.fileUidsForRemove;    // removed originals
    } else {
      // Create: send everything (kept + new) as FileDto[]
      const keptAsDto = Array.from(new Set(keptSavedUids)).map(uid => ({ fileUid: uid }));
      const map = new Map<string, FileDto>();
      for (const fd of [...keptAsDto, ...newUids]) map.set(fd.fileUid, fd);
      fileUidsForDto = Array.from(map.values());
      fileUidsForRemove = [];
    }

    const dto: GraphqlRestrictionInputDto = {
      restrictionId: formValue.id ?? uuidv4(),
      displayName: '',
      restrictionName: formValue.name,
      dateCreated: new Date().toISOString(),
      dateUpdated: new Date().toISOString(),
      validFrom: toIso(validFrom),
      validTo: toIso(validTo),
      banTestTrack: formValue.banTestTrack,
      banPublicRoad: formValue.banOpenRoad,
      active: !formValue.active,
      cause: formValue.causing,
      risk: formValue.risk,
      consequence: formValue.consequence,
      precondition: formValue.precondition,
      operatedRestriction: formValue.operatedRestriction,
      removingCondition: formValue.removingCondition,
      additionalInformation: formValue.additionalInformation,
      createdBy: isEdit ? (this.editRestriction?.createdBy ?? userName) : userName,
      modifiedBy: userName,
      links: Array.isArray(formValue.links) ? formValue.links.join(',') : '',
      responsible: isEdit ? (this.editRestriction?.createdBy ?? userName) : userName,
      approvedBy: formValue.approvedBy,

      // Files (can be null in edit to mean "no additions")
      fileUids: fileUidsForDto as any,
      fileUidsForRemove,

      // Vehicles (can be null in edit to mean "no additions")
      vehicles: vehiclesForDto as any,
      removedVehicleName: removedVehicleNameForDto,

      ...(isEdit ? (() => {
        const { additions, removals } = this.diffFpcsForEdit();
        return {
          fpcs: additions.length ? additions : undefined,
          fpcIdsForRemove: removals,
        };
      })() : {
        fpcs: this.selectedFpcs.map(f => ({ fpcId: f.fpcCode, name: f.name, category: '' })),
        fpcIdsForRemove: [],
      }),
    };

    // If your schema does not allow nulls for these, OMIT them instead:
    // if (isEdit && fileUidsForDto === null) delete (dto as any).fileUids;   // OMIT FIELD IF null
    // if (isEdit && vehiclesForDto === null) delete (dto as any).vehicles;   // OMIT FIELD IF null

    // Remove only undefined keys (we keep possible nulls intentionally)
    Object.keys(dto).forEach(k => (dto as any)[k] === undefined && delete (dto as any)[k]);
    return dto;
  }
}
